"use client";

import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

export function AndroidBackButtonHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);

  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    // Only handle hardware/gesture back button on native platforms (primarily Android)
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const listenerPromise = CapacitorApp.addListener("backButton", ({ canGoBack }) => {
      // 1. Check if any open modal/dialog/sheet/alertdialog exists in the DOM and dismiss it first
      const openModal = document.querySelector(
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="dialog"], [role="alertdialog"]'
      );
      if (openModal) {
        const escapeEvent = new KeyboardEvent("keydown", {
          key: "Escape",
          code: "Escape",
          keyCode: 27,
          which: 27,
          bubbles: true,
          cancelable: true,
        });
        document.dispatchEvent(escapeEvent);
        return;
      }

      // Check for any open menu / popover dropdown
      const openMenu = document.querySelector('[role="menu"][data-state="open"], [role="menu"]');
      if (openMenu) {
        const escapeEvent = new KeyboardEvent("keydown", {
          key: "Escape",
          code: "Escape",
          keyCode: 27,
          which: 27,
          bubbles: true,
          cancelable: true,
        });
        document.dispatchEvent(escapeEvent);
        return;
      }

      const currentPath = pathnameRef.current;
      const isRootPath = currentPath === "/" || currentPath === "/login" || currentPath === "/signup";

      if (isRootPath) {
        // Send app to background instead of popping WebView history into prior auth screens or looping
        CapacitorApp.minimizeApp();
      } else {
        // For inner routes (e.g. /settings, /legal/*, /subscription-test)
        if (canGoBack && window.history.length > 1) {
          navigate(-1);
        } else {
          navigate("/", { replace: true });
        }
      }
    });

    return () => {
      listenerPromise.then((handle) => handle.remove());
    };
  }, [navigate]);

  return null;
}
